import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Badge, Table, Spinner, Alert,
    Nav, ListGroup
} from "react-bootstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import { formatCurrency, formatDate } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { 
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
    ResponsiveContainer, PieChart, Pie, Cell, Legend 
} from 'recharts';

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { API_BASE_URL } from "../constants/api";

// Composant de notification
const Notification = ({ show, variant, message, onClose, autoHide = true }) => {
  const [visible, setVisible] = useState(show);

  useEffect(() => {
    setVisible(show);
  }, [show]);

  useEffect(() => {
    if (visible && autoHide) {
      const timer = setTimeout(() => {
        setVisible(false);
        if (onClose) onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [visible, autoHide, onClose]);

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '20px',
      right: '20px',
      zIndex: 9999,
      minWidth: '350px',
      maxWidth: '450px',
      animation: 'slideIn 0.3s ease-out'
    }}>
      <Alert 
        variant={variant} 
        onClose={() => {
          setVisible(false);
          if (onClose) onClose();
        }} 
        dismissible
        className="shadow-lg border-0"
        style={{
          borderRadius: '12px',
          borderLeft: `4px solid ${
            variant === 'success' ? '#10b981' :
            variant === 'danger' ? '#ef4444' :
            variant === 'warning' ? '#f59e0b' :
            variant === 'info' ? '#3b82f6' : '#6c757d'
          }`,
          marginBottom: '10px'
        }}
      >
        <div className="d-flex align-items-center">
          <div className="me-3">
            {variant === 'success' && <i className="ri-checkbox-circle-line fs-4 text-success"></i>}
            {variant === 'danger' && <i className="ri-error-warning-line fs-4 text-danger"></i>}
            {variant === 'warning' && <i className="ri-alert-line fs-4 text-warning"></i>}
            {variant === 'info' && <i className="ri-information-line fs-4 text-info"></i>}
          </div>
          <div className="flex-grow-1">
            <strong className="d-block mb-1">
              {variant === 'success' && 'Succès'}
              {variant === 'danger' && 'Erreur'}
              {variant === 'warning' && 'Attention'}
              {variant === 'info' && 'Information'}
            </strong>
            <p className="mb-0 small">{message}</p>
          </div>
        </div>
      </Alert>
    </div>
  );
};

// Styles pour les animations
const style = document.createElement('style');
style.innerHTML = `
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;
document.head.appendChild(style);

export default function DetailClient() {
    const { id } = useParams();
    const navigate = useNavigate();
  
    const [stats, setStats] = useState(null);
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('factures');
    const [contrat, setContrat] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    
    // États pour les notifications
    const [notifications, setNotifications] = useState([]);

    const addNotification = (message, variant = 'info') => {
        const id = Date.now() + Math.random();
        setNotifications(prev => [...prev, { id, message, variant }]);
    };

    const removeNotification = (id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    };

    const showSuccess = (message) => addNotification(message, 'success');
    const showError = (message) => addNotification(message, 'danger');
    const showWarning = (message) => addNotification(message, 'warning');
    const showInfo = (message) => addNotification(message, 'info');

    useEffect(() => {
        fetchClientDetails();
        fetchClientStats();
        fetchContrat();
    }, [id]);

    const fetchClientDetails = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setClient(result.data);
        } catch (err) {
            setError(err.message);
            showError("Erreur lors du chargement du client");
        } finally {
            setLoading(false);
        }
    };

    const fetchClientStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php?action=statistiques&id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };


    const getStatusInfo = () => {
        if (!client) return { color: 'secondary', label: 'Inconnu', icon: 'ri-question-line' };
        if (client.encours > 0) return { color: 'warning', label: 'En cours', icon: 'ri-time-line' };
        if (client.total_achats > 0) return { color: 'success', label: 'Actif', icon: 'ri-checkbox-circle-line' };
        return { color: 'secondary', label: 'Inactif', icon: 'ri-user-unfollow-line' };
    };

    const getSoldeClass = (solde) => {
        if (solde > 0) return 'text-danger';
        if (solde < 0) return 'text-success';
        return 'text-secondary';
    };

    const statusInfo = getStatusInfo();

    // Données pour les graphiques
    const activityData = client?.factures?.slice(0, 6).map(f => ({
        mois: formatDate(f.date_facture).substring(3),
        montant: f.montant
    })) || [];

    const pieData = [
        { name: 'Payé', value: (client?.total_achats || 0) - (client?.encours || 0) },
        { name: 'Impayé', value: client?.encours || 0 }
    ];

    const COLORS = ['#10b981', '#ef4444'];

    // =====================================================
    // FONCTIONS D'EXPORT
    // =====================================================
    
    const genererReleveCompte = async () => {
        try {
            showInfo("Génération du PDF en cours...");
            
            const token = localStorage.getItem("token");
            
            const response = await fetch(
                `${API_BASE_URL}/factures.php?client_id=${client.id_client}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const result = await response.json();
            const factures = result.data || [];
            
            // Créer le document
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });
            
            // === EN-TÊTE ===
            doc.setFillColor(67, 97, 238);
            doc.rect(0, 0, 210, 35, 'F');
            
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(22);
            doc.setFont("helvetica", "bold");
            doc.text("RELEVÉ DE COMPTE", 105, 20, { align: 'center' });
            
            doc.setFontSize(10);
            doc.text(client.raison_sociale, 105, 28, { align: 'center' });
            doc.text(`Code: ${client.code_client}`, 105, 33, { align: 'center' });
            
            // === INFORMATIONS CLIENT ===
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.text("Informations client", 15, 45);
            
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.text(`Adresse: ${client.adresse || 'Non renseignée'}`, 15, 52);
            doc.text(`Téléphone: ${client.telephone || 'Non renseigné'}`, 15, 58);
            doc.text(`Email: ${client.email || 'Non renseigné'}`, 15, 64);
            
            const dateDebut = new Date();
            dateDebut.setMonth(dateDebut.getMonth() - 12);
            const dateFin = new Date();
            
            doc.text(`Période du ${formatDate(dateDebut)} au ${formatDate(dateFin)}`, 120, 52);
            doc.text(`Date d'édition: ${formatDate(new Date())}`, 120, 58);
            
            // === RÉSUMÉ FINANCIER ===
            doc.setFillColor(240, 240, 240);
            doc.rect(15, 75, 180, 30, 'F');
            
            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.text("RÉSUMÉ FINANCIER", 15, 72);
            
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            
            const totalAchats = (client.total_achats || 0).toLocaleString('fr-FR') + ' FCFA';
            const encours = (client.encours || 0).toLocaleString('fr-FR') + ' FCFA';
            const totalRegle = ((client.total_achats || 0) - (client.encours || 0)) + ' FCFA';
            const plafond = (client.plafond_credit || 0).toLocaleString('fr-FR') + ' FCFA';
            
            doc.text("Total achats:", 20, 85);
            doc.text(totalAchats, 70, 85);
            doc.text("Encours actuel:", 120, 85);
            doc.text(encours, 190, 85, { align: 'right' });
            
            doc.text("Total réglé:", 20, 95);
            doc.text(totalRegle, 70, 95);
            doc.text("Plafond crédit:", 120, 95);
            doc.text(plafond, 190, 95, { align: 'right' });
            
            // === TABLEAU DES FACTURES ===
            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.text("DÉTAIL DES FACTURES", 15, 115);
            
            const facturesValides = factures.filter(f => f.numero_facture);
            
            const tableColumn = ["N° Facture", "Date", "Échéance", "Montant", "Réglé", "Solde", "Statut"];
            const tableRows = [];
            
            facturesValides.forEach(facture => {
                const montant = (facture.montant_ttc || facture.montant || 0).toLocaleString('fr-FR') + ' FCFA';
                const montantRegle = (facture.montant_regle || 0).toLocaleString('fr-FR') + ' FCFA';
                const solde = ((facture.montant_ttc || facture.montant || 0) - (facture.montant_regle || 0)) + ' FCFA';
                
                let statut = "Impayée";
                if (facture.statut === 'payee' || (facture.montant_regle >= (facture.montant_ttc || facture.montant))) statut = "Payée";
                else if (facture.statut === 'payee_partielle' || (facture.montant_regle > 0)) statut = "Partielle";
                
                const factureData = [
                    facture.numero_facture,
                    formatDate(facture.date_facture),
                    formatDate(facture.date_echeance),
                    montant,
                    montantRegle,
                    solde,
                    statut
                ];
                tableRows.push(factureData);
            });
            
            autoTable(doc, {
                startY: 120,
                head: [tableColumn],
                body: tableRows,
                theme: 'striped',
                styles: { 
                    fontSize: 8, 
                    cellPadding: 3,
                    lineColor: [200, 200, 200],
                    lineWidth: 0.1
                },
                headStyles: { 
                    fillColor: [67, 97, 238], 
                    textColor: 255, 
                    fontStyle: 'bold',
                    halign: 'center'
                },
                alternateRowStyles: { fillColor: [245, 245, 245] },
                columnStyles: {
                    0: { cellWidth: 30, halign: 'left' },
                    1: { cellWidth: 20, halign: 'center' },
                    2: { cellWidth: 20, halign: 'center' },
                    3: { cellWidth: 25, halign: 'right' },
                    4: { cellWidth: 25, halign: 'right' },
                    5: { cellWidth: 25, halign: 'right' },
                    6: { cellWidth: 25, halign: 'center' }
                }
            });
            
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(150, 150, 150);
                doc.text(
                    `Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`,
                    105,
                    285,
                    { align: 'center' }
                );
                doc.text(`Page ${i} sur ${pageCount}`, 195, 285, { align: 'right' });
                doc.text("Système de Gestion", 15, 285);
            }
            
            doc.save(`releve_compte_${client.code_client}_${Date.now()}.pdf`);
            showSuccess("PDF généré avec succès !");
            
        } catch (error) {
            console.error("Erreur génération relevé:", error);
            showError("Erreur lors de la génération du PDF : " + error.message);
        }
    };

    const exporterFacturesExcel = async () => {
        try {
            showInfo("Génération du fichier Excel en cours...");
            
            const token = localStorage.getItem("token");
            
            const response = await fetch(
                `${API_BASE_URL}/factures.php?client_id=${client.id_client}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const result = await response.json();
            const factures = result.data || [];
            
            const facturesValides = factures.filter(f => f.numero_facture);
            
            const dataExcel = facturesValides.map(f => {
                const montant = parseFloat(f.montant_ttc || f.montant) || 0;
                const montantRegle = parseFloat(f.montant_regle) || 0;
                const solde = montant - montantRegle;
                
                return {
                    'N° Facture': f.numero_facture,
                    'Date facture': formatDate(f.date_facture),
                    'Date échéance': formatDate(f.date_echeance),
                    'Montant TTC': montant.toLocaleString('fr-FR') + ' FCFA',
                    'Montant réglé': montantRegle.toLocaleString('fr-FR') + ' FCFA',
                    'Solde': solde.toLocaleString('fr-FR') + ' FCFA',
                    'Statut': f.statut === 'payee' ? 'Payée' : 
                             f.statut === 'payee_partielle' ? 'Partielle' : 'Impayée',
                    'Mode paiement': f.mode_paiement || 'Non spécifié'
                };
            });
            
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(dataExcel);
            
            const colWidths = [
                { wch: 18 }, { wch: 14 }, { wch: 14 }, 
                { wch: 18 }, { wch: 18 }, { wch: 18 }, 
                { wch: 14 }, { wch: 20 }
            ];
            ws['!cols'] = colWidths;
            
            const range = XLSX.utils.decode_range(ws['!ref']);
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const address = XLSX.utils.encode_col(C) + "1";
                if (!ws[address]) continue;
                ws[address].s = {
                    font: { bold: true, color: { rgb: "FFFFFF" } },
                    fill: { fgColor: { rgb: "4361EE" } },
                    alignment: { horizontal: "center", vertical: "center" }
                };
            }
            
            const resumeData = [
                ['RÉSUMÉ CLIENT', ''],
                ['Raison sociale', client.raison_sociale],
                ['Code client', client.code_client],
                ['Téléphone', client.telephone || '-'],
                ['Email', client.email || '-'],
                [''],
                ['INDICATEURS FINANCIERS', ''],
                ['Total achats', (parseFloat(client.total_achats) || 0).toLocaleString('fr-FR') + ' FCFA'],
                ['Total réglé', ((parseFloat(client.total_achats) || 0) - (parseFloat(client.encours) || 0)).toLocaleString('fr-FR') + ' FCFA'],
                ['Encours', (parseFloat(client.encours) || 0).toLocaleString('fr-FR') + ' FCFA'],
                ['Plafond crédit', (parseFloat(client.plafond_credit) || 0).toLocaleString('fr-FR') + ' FCFA'],
                ['Nombre factures', facturesValides.length],
                [''],
                [`Export généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, '']
            ];
            
            const wsResume = XLSX.utils.aoa_to_sheet(resumeData);
            wsResume['!cols'] = [{ wch: 25 }, { wch: 30 }];
            
            XLSX.utils.book_append_sheet(wb, wsResume, "Résumé");
            XLSX.utils.book_append_sheet(wb, ws, "Factures");
            
            XLSX.writeFile(wb, `factures_${client.code_client}_${Date.now()}.xlsx`);
            showSuccess("Fichier Excel généré avec succès !");
            
        } catch (error) {
            console.error("Erreur export Excel:", error);
            showError("Erreur lors de l'export Excel : " + error.message);
        }
    };

   // ✅ Version avec localStorage comme source principale
// ✅ Version avec localStorage comme source principale et possibilité de mise à jour
const uploadContrat = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validation
    if (file.size > 10 * 1024 * 1024) {
        showError("Le fichier est trop volumineux (max 10 Mo)");
        return;
    }
    
    const allowedExtensions = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
    const extension = file.name.split('.').pop().toLowerCase();
    
    if (!allowedExtensions.includes(extension)) {
        showError("Type de fichier non autorisé");
        return;
    }
    
    setUploading(true);
    
    const formData = new FormData();
    formData.append('contrat', file);
    formData.append('id_client', client.id_client);
    
    const interval = setInterval(() => {
        setUploadProgress(prev => {
            if (prev >= 90) {
                clearInterval(interval);
                return 90;
            }
            return prev + 10;
        });
    }, 200);
    
    try {
        const token = localStorage.getItem("token");
        
        const response = await fetch(
            `${API_BASE_URL}/upload_contrat.php`,
            {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            }
        );
        
        clearInterval(interval);
        setUploadProgress(100);
        
        const result = await response.json();
        
        if (result.success) {
            // ✅ Créer l'objet contrat
            const contratData = {
                nom: result.nom || file.name,
                date: result.date || new Date().toISOString(),
                url: result.url || URL.createObjectURL(file) // Fallback URL locale
            };
            
            setContrat(contratData);
            
            // ✅ Sauvegarder dans localStorage
            localStorage.setItem(`contrat_${client.id_client}`, JSON.stringify(contratData));
            
            showSuccess(contrat ? "Contrat mis à jour avec succès !" : "Contrat uploadé avec succès !");
        } else {
            showError(result.message || "Échec de l'upload");
        }
    } catch (error) {
        console.error("Erreur upload:", error);
        
        // ✅ En cas d'erreur, sauvegarder localement quand même
        const contratData = {
            nom: file.name,
            date: new Date().toISOString(),
            url: URL.createObjectURL(file), // URL locale temporaire
            local: true // Marquer comme local
        };
        
        setContrat(contratData);
        localStorage.setItem(`contrat_${client.id_client}`, JSON.stringify(contratData));
        
        showWarning(contrat ? "Contrat mis à jour localement (en attente de synchronisation)" : "Contrat sauvegardé localement (en attente de synchronisation)");
    } finally {
        clearInterval(interval);
        setUploading(false);
        setUploadProgress(0);
        event.target.value = '';
    }
};

// ✅ Fonction pour supprimer le contrat
const supprimerContrat = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce contrat ?")) {
        return;
    }
    
    try {
        const token = localStorage.getItem("token");
        
        // Appel API pour supprimer (à implémenter côté serveur)
        const response = await fetch(
            `${API_BASE_URL}/upload_contrat.php?action=delete&id_client=${client.id_client}`,
            {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            }
        );
        
        // Même si l'API échoue, on supprime du localStorage
        setContrat(null);
        localStorage.removeItem(`contrat_${client.id_client}`);
        
        showSuccess("Contrat supprimé avec succès");
        
    } catch (error) {
        console.error("Erreur suppression:", error);
        // En cas d'erreur, on supprime quand même du localStorage
        setContrat(null);
        localStorage.removeItem(`contrat_${client.id_client}`);
        showWarning("Contrat supprimé localement");
    }
};

// ✅ Version améliorée de fetchContrat
const fetchContrat = async () => {
    try {
        // ✅ 1. D'abord charger depuis localStorage pour l'affichage immédiat
        const savedContrat = localStorage.getItem(`contrat_${id}`);
        if (savedContrat) {
            setContrat(JSON.parse(savedContrat));
        }
        
        // ✅ 2. Ensuite essayer l'API pour avoir la version à jour
        const token = localStorage.getItem("token");
        const response = await fetch(
            `${API_BASE_URL}/upload_contrat.php?action=get_contrat&id_client=${id}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) {
                setContrat(result.data);
                localStorage.setItem(`contrat_${id}`, JSON.stringify(result.data));
            }
        }
    } catch (err) {
        console.error("Erreur chargement contrat:", err);
        // ✅ Garder les données localStorage en cas d'erreur
    }
};
    const visualiserContrat = () => {
        if (contrat?.url) {
            window.open(contrat.url, '_blank');
        } else {
            showWarning("Aucun contrat disponible");
        }
    };

    const downloadContrat = () => {
        if (contrat?.url) {
            saveAs(contrat.url, contrat.nom || 'contrat.pdf');
        } else {
            showWarning("Aucun contrat à télécharger");
        }
    };

 if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="loading-animation mb-4">
                                <div className="loading-ring"><div className="loading-ring-inner"></div></div>
                                <i className="ri-group-line loading-icon"></i>
                            </div>
                            <h5 className="fw-bold text-primary mb-2">Chargement des informations du client</h5>
                            <p className="text-muted small mb-0">Récupération des données en cours...</p>
                            <div className="mx-auto mt-4" style={{ width: '200px' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef' }}>
                                    <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%', background: 'linear-gradient(90deg, #4361ee, #10b981, #f59e0b)' }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-animation { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; background: linear-gradient(135deg, #4361ee, #10b981); animation: pulse-ring 1.5s ease-in-out infinite; }
                    .loading-ring-inner { position: absolute; top: 5px; left: 5px; right: 5px; bottom: 5px; border-radius: 50%; background: white; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: #4361ee; z-index: 2; animation: pulse-icon 1.5s ease-in-out infinite; }
                    @keyframes pulse-ring { 0% { transform: scale(0.9); opacity: 0.7; } 50% { transform: scale(1.1); opacity: 0.3; } 100% { transform: scale(0.9); opacity: 0.7; } }
                    @keyframes pulse-icon { 0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; } 50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.8; } }
                `}</style>
            </>
        );
    }

    if (error || !client) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error || "Client non trouvé"}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={() => navigate('/ventes/clients/liste')}>
                            <i className="ri-arrow-left-line me-2"></i>Retour à la liste
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                {/* Zone de notifications */}
                {notifications.map(notif => (
                    <Notification
                        key={notif.id}
                        show={true}
                        variant={notif.variant}
                        message={notif.message}
                        onClose={() => removeNotification(notif.id)}
                    />
                ))}

                <div className="container-fluid">
                    {/* Fil d'Ariane */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <Button 
                                    variant="link" 
                                    className="p-0 text-muted" 
                                    onClick={() => navigate('/ventes/clients/liste')}
                                >
                                    <i className="ri-arrow-left-line me-1"></i>Clients
                                </Button>
                                <span className="text-muted">/</span>
                                <span className="text-primary">Fiche client</span>
                            </div>
                        </Col>
                    </Row>

                    {/* En-tête avec carte profil */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <Card className="border-0 shadow-sm overflow-hidden">
                                <div className="bg-gradient-primary" style={{ height: '120px', background: '#EDEDED' }}></div>
                                <Card.Body className="position-relative" style={{ marginTop: '-60px' }}>
                                    <Row>
                                        <Col md={8}>
                                            <div className="d-flex align-items-end gap-4">
                                                <div className="bg-white rounded-4 shadow-lg p-2">
                                                    <div className="bg-light rounded-0 d-flex align-items-center justify-content-center" style={{ width: '100px', height: '100px' }}>
                                                        <i className="ri-user-3-line" style={{ fontSize: '48px', color: '#4361ee' }}></i>
                                                    </div>
                                                </div>
                                                <div className="mb-2">
                                                    <h1 className="display-6 fw-bold mb-1">{client.raison_sociale}</h1>
                                                    <div className="d-flex align-items-center gap-3">
                                                        <Badge bg={statusInfo.color} className="px-3 py-2">
                                                            <i className={`${statusInfo.icon} me-1`}></i>
                                                            {statusInfo.label}
                                                        </Badge>
                                                        <span className="text-muted">
                                                            <i className="ri-barcode-line me-1"></i>
                                                            Code: {client.code_client}
                                                        </span>
                                                        <span className="text-muted">
                                                            <i className="ri-calendar-line me-1"></i>
                                                            Client depuis {formatDate(client.date_creation)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="d-flex justify-content-end gap-2">
                                                <Button 
                                                    variant="outline-primary" 
                                                    onClick={() => navigate(`/ventes/factures/nouveau?client=${client.id_client}`)}
                                                >
                                                    <i className="ri-file-copy-line me-2"></i>
                                                    Nouvelle facture
                                                </Button>
                                                <Button 
                                                    variant="primary"
                                                    as={Link}
                                                    to={`/ventes/clients/modifier/${client.id_client}`}
                                                >
                                                    <i className="ri-pencil-line me-2"></i>
                                                    Modifier
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Cartes KPI */}
          
<Row className="g-3 mb-4">
    <Col sm="6" xl="3">
        <div className="kpi-card kpi-primary">
            <div className="kpi-icon">
                <i className="ri-shopping-bag-3-line fs-24"></i>
            </div>
            <div className="kpi-content">
                <span className="kpi-label">Total achats</span>
                <h3 className="kpi-value">{formatCurrency(client.total_achats || 0)}</h3>
                <span className="kpi-unit">{client.nb_factures || 0} factures</span>
                <div className="kpi-progress mt-2">
                    <div className="progress" style={{ height: '3px' }}>
                        <div className="progress-bar bg-primary" style={{ width: '75%' }}></div>
                    </div>
                </div>
            </div>
        </div>
    </Col>

    <Col sm="6" xl="3">
        <div className="kpi-card kpi-danger">
            <div className="kpi-icon">
                <i className="ri-bank-card-line fs-24"></i>
            </div>
            <div className="kpi-content">
                <span className="kpi-label">Encours</span>
                <h3 className={`kpi-value ${getSoldeClass(client.encours)}`}>
                    {formatCurrency(client.encours || 0)}
                </h3>
                <span className="kpi-unit">solde à payer</span>
                <div className="kpi-progress mt-2">
                    <div className="progress" style={{ height: '3px' }}>
                        <div className="progress-bar bg-danger" style={{ width: client.plafond_credit ? `${((client.encours || 0) / client.plafond_credit) * 100}%` : '0%' }}></div>
                    </div>
                </div>
            </div>
        </div>
    </Col>

    <Col sm="6" xl="3">
        <div className="kpi-card kpi-success">
            <div className="kpi-icon">
                <i className="ri-secure-payment-line fs-24"></i>
            </div>
            <div className="kpi-content">
                <span className="kpi-label">Plafond crédit</span>
                <h3 className="kpi-value text-success">{formatCurrency(client.plafond_credit || 0)}</h3>
                <span className="kpi-unit">
                    {client.plafond_credit ? `${Math.round(((client.encours || 0) / client.plafond_credit) * 100)}% utilisé` : 'Non défini'}
                </span>
                <div className="kpi-progress mt-2">
                    <div className="progress" style={{ height: '3px' }}>
                        <div className="progress-bar bg-success" style={{ width: client.plafond_credit ? `${((client.encours || 0) / client.plafond_credit) * 100}%` : '0%' }}></div>
                    </div>
                </div>
            </div>
        </div>
    </Col>

    <Col sm="6" xl="3">
        <div className="kpi-card kpi-info">
            <div className="kpi-icon">
                <i className="ri-calendar-check-line fs-24"></i>
            </div>
            <div className="kpi-content">
                <span className="kpi-label">Dernier achat</span>
                <h3 className="kpi-value text-info">
                    {client.derniere_vente ? formatDate(client.derniere_vente) : 'Jamais'}
                </h3>
                <span className="kpi-unit">
                    {client.derniere_vente ? `${Math.round((new Date() - new Date(client.derniere_vente)) / (1000 * 60 * 60 * 24))} jours` : '-'}
                </span>
                <div className="kpi-progress mt-2">
                    <div className="progress" style={{ height: '3px' }}>
                        <div className="progress-bar bg-info" style={{ width: client.derniere_vente ? '100%' : '0%' }}></div>
                    </div>
                </div>
            </div>
        </div>
    </Col>
</Row>

<style>{`
    /* KPI Cards */
    .kpi-card {
        background: white;
        border-radius: 0px;
        padding: 1rem;
        display: flex;
        align-items: center;
        gap: 1rem;
        box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        transition: all 0.2s ease;
        height: 100%;
        border-left: 2px solid;
    }
    .kpi-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(0,0,0,0.08);
    }
    .kpi-primary { border-left-color: #4361ee; }
    .kpi-danger { border-left-color: #ef4444; }
    .kpi-success { border-left-color: #10b981; }
    .kpi-info { border-left-color: #0dcaf0; }
    
    .kpi-icon {
        width: 48px;
        height: 48px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .kpi-primary .kpi-icon { background: #e8f0fe; color: #4361ee; }
    .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
    .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
    .kpi-info .kpi-icon { background: #e2f3f5; color: #0dcaf0; }
    
    .kpi-content {
        flex: 1;
    }
    .kpi-label {
        font-size: 0.7rem;
        text-transform: uppercase;
        color: #6c757d;
        font-weight: 600;
        letter-spacing: 0.5px;
    }
    .kpi-value {
        font-size: 1.1rem;
        font-weight: 700;
        margin: 0.25rem 0;
        color: #1a3c5e;
    }
    .kpi-unit {
        font-size: 0.7rem;
        color: #6c757d;
    }
    .kpi-progress {
        opacity: 0.6;
        transition: opacity 0.3s ease;
    }
    .kpi-card:hover .kpi-progress {
        opacity: 1;
    }
    .progress {
        background-color: #e9ecef;
        border-radius: 10px;
        overflow: hidden;
    }
    .progress-bar {
        border-radius: 10px;
        transition: width 0.6s ease;
    }
    .fs-24 {
        font-size: 1.5rem;
    }
`}</style>

                    {/* Onglets */}
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <Nav variant="tabs" defaultActiveKey="factures" onSelect={(k) => setActiveTab(k)}>
                                <Nav.Item>
                                    <Nav.Link eventKey="factures">
                                        <i className="ri-file-list-line me-2"></i>
                                        Factures et paiements
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="informations">
                                        <i className="ri-information-line me-2"></i>
                                        Informations détaillées
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="statistiques">
                                        <i className="ri-bar-chart-line me-2"></i>
                                        Statistiques
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="documents">
                                        <i className="ri-file-copy-line me-2"></i>
                                        Documents
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>
                        </Card.Header>
                        <Card.Body>
                            {/* Onglet Factures */}
                            {activeTab === 'factures' && (
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h5 className="mb-0">Historique des factures</h5>
                                        <Button 
                                            variant="outline-primary" 
                                            size="sm"
                                            onClick={() => navigate(`/ventes/factures/nouveau?client=${client.id_client}`)}
                                        >
                                            <i className="ri-add-line me-1"></i>
                                            Nouvelle facture
                                        </Button>
                                    </div>
                                    
                                    {(() => {
                                        const facturesValides = client.factures?.filter(f => f.numero_facture) || [];
                                        
                                        return facturesValides.length > 0 ? (
                                            <div className="table-responsive">
                                                <Table hover className="align-middle">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th>Facture</th>
                                                            <th>Date</th>
                                                            <th>Échéance</th>
                                                            <th className="text-end">Montant TTC</th>
                                                            <th className="text-end">Réglé</th>
                                                            <th className="text-end">Solde</th>
                                                            <th>Statut</th>
                                                            <th className="text-center">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {facturesValides.map(facture => (
                                                            <tr key={facture.id_facture}>
                                                                <td>
                                                                    <Link to={`/ventes/facturesDetails/${facture.id_facture}`} className="text-primary">
                                                                        {facture.numero_facture}
                                                                    </Link>
                                                                </td>
                                                                <td>{formatDate(facture.date_facture)}</td>
                                                                <td>
                                                                    <span className={new Date(facture.date_echeance) < new Date() && facture.reste > 0 ? 'text-danger' : ''}>
                                                                        {formatDate(facture.date_echeance)}
                                                                    </span>
                                                                </td>
                                                                <td className="text-end">{formatCurrency(facture.montant)}</td>
                                                                <td className="text-end text-success">{formatCurrency(facture.montant_regle || 0)}</td>
                                                                <td className={`text-end fw-bold ${facture.reste > 0 ? 'text-danger' : 'text-success'}`}>
                                                                    {formatCurrency(facture.reste || 0)}
                                                                </td>
                                                                <td>
                                                                    <Badge bg={
                                                                        facture.statut === 'payee' ? 'success' :
                                                                        facture.statut === 'payee_partielle' ? 'warning' : 'danger'
                                                                    }>
                                                                        {facture.statut === 'payee' ? 'Payée' :
                                                                         facture.statut === 'payee_partielle' ? 'Partielle' : 'Impayée'}
                                                                    </Badge>
                                                                </td>
                                                                <td className="text-center">
                                                                    <Button 
                                                                        variant="outline-info" 
                                                                        size="sm"
                                                                        onClick={() => navigate(`/ventes/NouveauReglement?facture=${facture.id_facture}&client=${client.id_client}`)}
                                                                        title="Saisir un règlement"
                                                                    >
                                                                        <i className="ri-bank-card-line"></i>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <i className="ri-file-list-line fs-1 text-muted mb-3"></i>
                                                <h6>Aucune facture</h6>
                                                <p className="text-muted small">Ce client n'a pas encore de facture</p>
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}

                            {/* Onglet Informations */}
                            {activeTab === 'informations' && (
                                <Row>
                                    <Col md={6}>
                                        <Card className="border-0 bg-light">
                                            <Card.Body>
                                                <h6 className="mb-3">
                                                    <i className="ri-contacts-line me-2 text-primary"></i>
                                                    Coordonnées
                                                </h6>
                                                <ListGroup variant="flush" className="bg-transparent">
                                                    <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between">
                                                        <span className="text-muted">Téléphone</span>
                                                        <span className="fw-semibold">{client.telephone || 'Non renseigné'}</span>
                                                    </ListGroup.Item>
                                                    <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between">
                                                        <span className="text-muted">Email</span>
                                                        <span className="fw-semibold">{client.email || 'Non renseigné'}</span>
                                                    </ListGroup.Item>
                                                    <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between">
                                                        <span className="text-muted">Adresse</span>
                                                        <span className="fw-semibold">{client.adresse || 'Non renseignée'}</span>
                                                    </ListGroup.Item>
                                                </ListGroup>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col md={6}>
                                        <Card className="border-0 bg-light">
                                            <Card.Body>
                                                <h6 className="mb-3">
                                                    <i className="ri-briefcase-line me-2 text-primary"></i>
                                                    Identifiants juridiques
                                                </h6>
                                                <ListGroup variant="flush" className="bg-transparent">
                                                    <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between">
                                                        <span className="text-muted">Numéro compte</span>
                                                        <span className="fw-semibold">{client.numero_compte || '-'}</span>
                                                    </ListGroup.Item>
                                                    <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between">
                                                        <span className="text-muted">ICE</span>
                                                        <span className="fw-semibold">{client.ice || '-'}</span>
                                                    </ListGroup.Item>
                                                    <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between">
                                                        <span className="text-muted">Patente</span>
                                                        <span className="fw-semibold">{client.patente || '-'}</span>
                                                    </ListGroup.Item>
                                                    <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between">
                                                        <span className="text-muted">RC</span>
                                                        <span className="fw-semibold">{client.rc || '-'}</span>
                                                    </ListGroup.Item>
                                                    <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between">
                                                        <span className="text-muted">IF</span>
                                                        <span className="fw-semibold">{client.identifiant_fiscal || '-'}</span>
                                                    </ListGroup.Item>
                                                    <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between">
                                                        <span className="text-muted">CNSS</span>
                                                        <span className="fw-semibold">{client.cnss || '-'}</span>
                                                    </ListGroup.Item>
                                                </ListGroup>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            )}

                            {/* Onglet Statistiques */}
                            {activeTab === 'statistiques' && (
                                <Row>
                                    <Col md={6}>
                                        <Card className="border-0 mb-3">
                                            <Card.Body>
                                                <h6 className="mb-3">Évolution des achats</h6>
                                                <ResponsiveContainer width="100%" height={250}>
                                                    <AreaChart data={activityData}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="mois" />
                                                        <YAxis />
                                                        <RechartsTooltip />
                                                        <Area type="monotone" dataKey="montant" stroke="#4361ee" fill="#4361ee" fillOpacity={0.3} />
                                                    </AreaChart>
                                                </ResponsiveContainer>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col md={6}>
                                        <Card className="border-0 mb-3">
                                            <Card.Body>
                                                <h6 className="mb-3">Répartition payé / impayé</h6>
                                                {pieData[0].value + pieData[1].value > 0 ? (
                                                    <ResponsiveContainer width="100%" height={250}>
                                                        <PieChart>
                                                            <Pie
                                                                data={pieData}
                                                                cx="50%"
                                                                cy="50%"
                                                                innerRadius={60}
                                                                outerRadius={80}
                                                                fill="#8884d8"
                                                                paddingAngle={5}
                                                                dataKey="value"
                                                            >
                                                                {pieData.map((entry, index) => (
                                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                                ))}
                                                            </Pie>
                                                            <RechartsTooltip />
                                                            <Legend />
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                ) : (
                                                    <div className="text-center py-5">
                                                        <p className="text-muted">Aucune donnée disponible</p>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col md={12}>
                                        <Card className="border-0">
                                            <Card.Body>
                                                <h6 className="mb-3">Indicateurs de performance</h6>
                                                <Row className="g-3">
                                                    <Col md={3}>
                                                        <div className="border rounded-0 p-3 text-center">
                                                            <div className="text-muted small mb-2">Ticket moyen</div>
                                                            <h5 className="mb-0 text-primary">
                                                                {client.moyenne_factures ? formatCurrency(client.moyenne_factures) : formatCurrency(0)}
                                                            </h5>
                                                        </div>
                                                    </Col>
                                                    <Col md={3}>
                                                        <div className="border rounded-0 p-3 text-center">
                                                            <div className="text-muted small mb-2">Délai de paiement</div>
                                                            <h5 className="mb-0 text-warning">
                                                                {client.delai_moyen || 0} jours
                                                            </h5>
                                                        </div>
                                                    </Col>
                                                    <Col md={3}>
                                                        <div className="border rounded-0 p-3 text-center">
                                                            <div className="text-muted small mb-2">Taux de fidélité</div>
                                                            <h5 className="mb-0 text-success">
                                                                {client.fidelite || 0}%
                                                            </h5>
                                                        </div>
                                                    </Col>
                                                    <Col md={3}>
                                                        <div className="border rounded-0 p-3 text-center">
                                                            <div className="text-muted small mb-2">Commandes en cours</div>
                                                            <h5 className="mb-0 text-info">
                                                                {client.commandes_en_cours || 0}
                                                            </h5>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            )}

                            {/* Onglet Documents */}
                            {activeTab === 'documents' && (
                                <Row className="g-4">
                                    {/* Carte Relevé de compte */}
                                    <Col md={4}>
                                        <Card className="border-0 shadow-sm h-100" style={{
                                            borderRadius: '16px',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                                            <Card.Body className="p-4 text-center">
                                                <div className="bg-light p-4 rounded-4 mb-3 d-inline-block">
                                                    <i className="ri-file-pdf-line fs-48 text-danger"></i>
                                                </div>
                                                <h5 className="fw-bold">Relevé de compte</h5>
                                                <p className="text-muted small mb-3">
                                                    {client.raison_sociale} • Période 12 mois
                                                </p>
                                                <Button 
                                                    variant="outline-danger" 
                                                    size="sm"
                                                    className="px-3"
                                                    onClick={genererReleveCompte}
                                                >
                                                    <i className="ri-download-line me-2"></i>
                                                    Télécharger PDF
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    </Col>

                                    {/* Carte Liste des factures */}
                                    <Col md={4}>
                                        <Card className="border-0 shadow-sm h-100" style={{
                                            borderRadius: '16px',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                                            <Card.Body className="p-4 text-center">
                                                <div className="bg-light p-4 rounded-4 mb-3 d-inline-block">
                                                    <i className="ri-file-excel-line fs-48 text-success"></i>
                                                </div>
                                                <h5 className="fw-bold">Liste des factures</h5>
                                                <p className="text-muted small mb-3">
                                                    Export Excel détaillé
                                                </p>
                                                <Button 
                                                    variant="outline-success" 
                                                    size="sm"
                                                    className="px-3"
                                                    onClick={exporterFacturesExcel}
                                                >
                                                    <i className="ri-download-line me-2"></i>
                                                    Export Excel
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    </Col>

                                    {/* Carte Contrat */}
                                 {/* Carte Contrat */}
<Col md={4}>
    <Card className="border-0 shadow-sm h-100" style={{
        borderRadius: '16px',
        transition: 'all 0.3s ease'
    }}
    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
        <Card.Body className="p-4 text-center">
            <div className="bg-light p-4 rounded-4 mb-3 d-inline-block">
                <i className="ri-file-text-line fs-48 text-primary"></i>
            </div>
            <h5 className="fw-bold">Contrat</h5>
            <p className="text-muted small mb-3">
                {contrat ? contrat.nom : 'Aucun contrat'}
            </p>
            
            {uploading && (
                <div className="mb-3">
                    <div className="progress" style={{ height: '6px' }}>
                        <div 
                            className="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
                            style={{ width: `${uploadProgress}%` }}
                        ></div>
                    </div>
                    <small className="text-muted mt-1">Upload en cours...</small>
                </div>
            )}
            
            <div className="d-flex justify-content-center gap-2 flex-wrap">
                {/* Input file caché */}
                <input
                    type="file"
                    id="contrat-upload"
                    className="d-none"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={uploadContrat}
                />
                
                {contrat ? (
                    <>
                        {/* <Button 
                            variant="outline-primary" 
                            size="sm"
                            className="px-3"
                            onClick={visualiserContrat}
                        >
                            <i className="ri-eye-line me-2"></i>
                            Voir
                        </Button> */}
                        <Button 
                            variant="outline-primary" 
                            size="sm"
                            className="px-3"
                            onClick={downloadContrat}
                        >
                            <i className="ri-download-line"></i>
                        </Button>
                        <Button 
                            variant="outline-warning" 
                            size="sm"
                            className="px-3"
                            onClick={() => document.getElementById('contrat-upload').click()}
                            disabled={uploading}
                            title="Remplacer le contrat"
                        >
                            <i className="ri-refresh-line me-2"></i>
                            Remplacer
                        </Button>
                       
                    </>
                ) : (
                    <Button 
                        variant="outline-primary" 
                        size="sm"
                        className="px-3"
                        onClick={() => document.getElementById('contrat-upload').click()}
                        disabled={uploading}
                    >
                        <i className="ri-upload-line me-2"></i>
                        Ajouter
                    </Button>
                )}
            </div>
            
            {contrat && (
                <div className="mt-3 text-muted small">
                    <i className="ri-calendar-line me-1"></i>
                    {contrat.local ? 'Sauvegardé localement le ' : 'Ajouté le '}
                    {formatDate(contrat.date)}
                </div>
            )}
        </Card.Body>
    </Card>
</Col>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>
                </div>

                {/* Styles */}
                <style>{`
                    .bg-gradient-primary {
                        background: linear-gradient(135deg, #b9babd 0%, #3a56d4 100%);
                    }
                    .fs-48 {
                        font-size: 48px;
                    }
                    .nav-tabs .nav-link {
                        color: #64748b;
                        border: none;
                        padding: 0.75rem 1.5rem;
                        font-weight: 500;
                    }
                    .nav-tabs .nav-link.active {
                        color: #4361ee;
                        background: none;
                        border-bottom: 2px solid #4361ee;
                    }
                    .nav-tabs .nav-link:hover {
                        color: #4361ee;
                        background: rgba(67, 97, 238, 0.05);
                    }.border-0 {
                    border-radius:0px !important}
                `}</style>

                <Footer />
            </div>
        </>
    );
}